import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import {
  Table, TableHead, TableBody, TableCell, TableRow,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Slide, MenuItem, CircularProgress, IconButton, Box, Icon,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import LocalHospitalOutlinedIcon from "@material-ui/icons/LocalHospitalOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SwitchToggle from "components/SwitchToggle/SwitchToggle";
import ConfirmModal from "components/Modal/modal";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import { FormErrors } from "../../components/Login/FormErrors";
import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

// Group levels (backend groupType enum) with UI labels.
const LEVELS = [
  { value: "EXECUTIVE", label: "Executive (top level)" },
  { value: "SVPO", label: "SVPO group" },
  { value: "VPO", label: "VPO group" },
  { value: "ADMIN", label: "Admin group" },
];
const levelLabel = (v) => (LEVELS.find((l) => l.value === v) || {}).label || v;
// Numeric hierarchy level per type — a group's only eligible children are exactly one level below.
const LEVEL_NUM = { EXECUTIVE: 1, SVPO: 2, VPO: 3, ADMIN: 4 };

const Transition = React.forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

export default class RegionGroups extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      groups: [],
      facilities: [],
      search: "",
      loading: false,
      modalStatus: false,
      saving: false,
      form: { groupName: "", groupType: "EXECUTIVE" },
      formErrors: { groupName: "" },
      facilityAttachments: [], // {facilityId, type}
      childGroupIds: [],
      pickFacilityId: "",
      pickFacilityType: "DIRECT",
      pickChildId: "",
      // view dialog
      viewOpen: false,
      viewDetail: null,
      viewLoading: false,
      // edit dialog
      editOpen: false,
      editGroup: null,       // the row being edited
      editDetail: null,      // GroupDetailDTO (children + facilities + managers)
      editName: "",
      editActive: true,      // pending status (applied on Update, not on toggle)
      editSaving: false,
      ePickFacilityId: "",
      ePickFacilityType: "DIRECT",
      ePickChildId: "",
      // delete confirmation
      confirmDeleteStatus: false,
      deleteTarget: null,
      deleting: false,
    };
  }

  componentDidMount() {
    this.fetchGroups();
    this.fetchFacilities();
  }

  // Pull a clean message out of an RFC-7807 error response (detail holds the reason).
  // Strips Spring's `409 CONFLICT "…"` wrapper so only the human reason is shown.
  extractError = async (response, fallback) => {
    try {
      const t = await response.text();
      if (!t) return fallback;
      let msg;
      try {
        const j = JSON.parse(t);
        msg = j.detail || j.title || fallback;
      } catch (e) {
        msg = t;
      }
      if (typeof msg === "string") {
        const m = msg.match(/^\d{3}\s.*?"([\s\S]*)"\s*$/);
        if (m) msg = m[1];
      }
      return msg || fallback;
    } catch (e) {
      return fallback;
    }
  };

  fetchGroups = async () => {
    this.setState({ loading: true });
    try {
      const r = await fetch(apiPath.groups, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ groups: Array.isArray(data) ? data : [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  fetchFacilities = async () => {
    try {
      const r = await fetch(`${apiPath.facilitiesList}?page=0&size=1000`, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ facilities: Array.isArray(data) ? data : [] });
    } catch (e) {
      /* ignore */
    }
  };

  notify = (message, type) => userService.showNotification({ title: "Region Groups", message, type });

  toggleStatus = async (g) => {
    try {
      const r = await fetch(`${apiPath.groups}/${g.id}/status?active=${!g.active}`, { method: "PUT", headers: generalAuthTokenHeader() });
      if (r.ok) this.fetchGroups();
      else this.notify("Could not update status", "danger");
    } catch (e) {
      this.notify("Server error", "danger");
    }
  };

  deleteGroup = (g) => this.setState({ confirmDeleteStatus: true, deleteTarget: g });

  confirmDeleteClose = () => this.setState({ confirmDeleteStatus: false, deleteTarget: null });

  doDeleteGroup = async () => {
    const g = this.state.deleteTarget;
    if (!g) return;
    this.setState({ deleting: true });
    try {
      const r = await fetch(`${apiPath.groups}/${g.id}`, { method: "DELETE", headers: generalAuthTokenHeader() });
      if (r.ok) { this.notify("Group deleted", "success"); this.fetchGroups(); this.fetchFacilities(); }
      else this.notify("Could not delete group", "danger");
    } catch (e) {
      this.notify("Server error", "danger");
    }
    this.setState({ deleting: false, confirmDeleteStatus: false, deleteTarget: null });
  };

  // ---------------------------------------------------------------- view / edit
  fetchDetail = async (id) => {
    const r = await fetch(`${apiPath.groups}/${id}`, { method: "GET", headers: generalAuthTokenHeader() });
    return r.ok ? r.json() : null;
  };

  openView = async (g) => {
    this.setState({ viewOpen: true, viewDetail: null, viewLoading: true });
    const detail = await this.fetchDetail(g.id);
    this.setState({ viewDetail: detail, viewLoading: false });
  };
  closeView = () => this.setState({ viewOpen: false });

  openEdit = async (g) => {
    this.setState({
      editOpen: true, editGroup: g, editDetail: null, editName: g.groupName || "",
      editActive: g.active !== false,
      editSaving: false, ePickFacilityId: "", ePickFacilityType: "DIRECT", ePickChildId: "",
    });
    const detail = await this.fetchDetail(g.id);
    this.setState({ editDetail: detail });
  };
  closeEdit = () => this.setState({ editOpen: false });

  refreshEditDetail = async () => {
    const { editGroup } = this.state;
    if (!editGroup) return;
    const detail = await this.fetchDetail(editGroup.id);
    this.setState({ editDetail: detail });
    this.fetchGroups();
    this.fetchFacilities();
  };

  // Status only changes locally; it is persisted when the user clicks Update.
  toggleEditActive = () => this.setState((s) => ({ editActive: !s.editActive }));

  // Single Update: persists name + status together (structural changes are applied live separately).
  saveEdit = async () => {
    const { editGroup, editName, editActive } = this.state;
    if (!editName || !editName.trim()) { this.notify("Group name is required", "danger"); return; }
    this.setState({ editSaving: true });
    try {
      const r = await fetch(`${apiPath.groups}/${editGroup.id}`, {
        method: "PUT", headers: generalAuthTokenHeader(),
        body: JSON.stringify({ groupName: editName.trim(), active: editActive }),
      });
      if (r.ok) {
        this.notify("Group updated", "success");
        this.setState({ editSaving: false, editOpen: false });
        this.fetchGroups();
      } else {
        this.notify(await this.extractError(r, "Could not update group"), "danger");
        this.setState({ editSaving: false });
      }
    } catch (e) {
      this.notify("Server error", "danger");
      this.setState({ editSaving: false });
    }
  };

  editAddChild = async () => {
    const { editGroup, ePickChildId } = this.state;
    if (!ePickChildId) return;
    try {
      const r = await fetch(`${apiPath.groups}/${editGroup.id}/children/${ePickChildId}`, { method: "POST", headers: generalAuthTokenHeader() });
      if (r.ok) { this.setState({ ePickChildId: "" }); this.refreshEditDetail(); }
      else { const t = await r.text(); this.notify(t || "Could not attach group", "danger"); }
    } catch (e) { this.notify("Server error", "danger"); }
  };
  editRemoveChild = async (childId) => {
    try {
      const r = await fetch(`${apiPath.groups}/children/${childId}`, { method: "DELETE", headers: generalAuthTokenHeader() });
      if (r.ok) this.refreshEditDetail(); else this.notify("Could not detach group", "danger");
    } catch (e) { this.notify("Server error", "danger"); }
  };

  editAddFacility = async () => {
    const { editGroup, ePickFacilityId } = this.state;
    if (!ePickFacilityId) return;
    try {
      const r = await fetch(`${apiPath.groups}/${editGroup.id}/facilities`, {
        method: "POST", headers: generalAuthTokenHeader(),
        body: JSON.stringify({ facilityId: Number(ePickFacilityId), type: "GROUP" }),
      });
      if (r.ok) { this.setState({ ePickFacilityId: "" }); this.refreshEditDetail(); }
      else { this.notify(await this.extractError(r, "Could not attach facility"), "danger"); }
    } catch (e) { this.notify("Server error", "danger"); }
  };
  editRemoveFacility = async (attachmentId) => {
    try {
      const r = await fetch(`${apiPath.groups}/facilities/${attachmentId}`, { method: "DELETE", headers: generalAuthTokenHeader() });
      if (r.ok) this.refreshEditDetail(); else this.notify("Could not detach facility", "danger");
    } catch (e) { this.notify("Server error", "danger"); }
  };

  modalOpen = () => this.setState({
    modalStatus: true,
    form: { groupName: "", groupType: "EXECUTIVE" },
    formErrors: { groupName: "" },
    facilityAttachments: [],
    childGroupIds: [],
    pickFacilityId: "",
    pickFacilityType: "DIRECT",
    pickChildId: "",
  });
  modalClose = () => this.setState({ modalStatus: false });

  handleField = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({
      form: { ...s.form, [name]: value },
      // Admin groups can't have child groups — drop any pending children when switching to Admin.
      childGroupIds: name === "groupType" && value === "ADMIN" ? [] : s.childGroupIds,
    }));
  };

  addFacility = () => {
    const { pickFacilityId, facilityAttachments } = this.state;
    if (!pickFacilityId) return;
    if (facilityAttachments.some((a) => String(a.facilityId) === String(pickFacilityId))) return;
    this.setState({
      facilityAttachments: [...facilityAttachments, { facilityId: Number(pickFacilityId), type: "GROUP" }],
      pickFacilityId: "",
    });
  };
  removeFacility = (idx) => this.setState((s) => ({ facilityAttachments: s.facilityAttachments.filter((_, i) => i !== idx) }));

  addChild = () => {
    const { pickChildId, childGroupIds } = this.state;
    if (!pickChildId) return;
    if (childGroupIds.some((id) => String(id) === String(pickChildId))) return;
    this.setState({ childGroupIds: [...childGroupIds, Number(pickChildId)], pickChildId: "" });
  };
  removeChild = (id) => this.setState((s) => ({ childGroupIds: s.childGroupIds.filter((x) => x !== id) }));

  facilityName = (id) => {
    const f = this.state.facilities.find((x) => String(x.facilityId) === String(id));
    return f ? f.facilityName : `#${id}`;
  };
  groupNameById = (id) => {
    const g = this.state.groups.find((x) => String(x.id) === String(id));
    return g ? `${g.groupName} (${levelLabel(g.groupType)})` : `#${id}`;
  };

  // Groups eligible to nest under a parent of `parentType`: exactly one level below, not already
  // parented, not the group itself, and not already added/attached.
  eligibleChildGroups = (parentType, addedIds, excludeId) => {
    const target = LEVEL_NUM[parentType] + 1;
    return this.state.groups.filter((g) =>
      g.id !== excludeId &&
      LEVEL_NUM[g.groupType] === target &&
      !g.parentGroupId &&
      !(addedIds || []).some((id) => String(id) === String(g.id))
    );
  };

  createGroup = async () => {
    const { form, facilityAttachments, childGroupIds } = this.state;
    if (!form.groupName || !form.groupName.trim()) {
      this.setState({ formErrors: { groupName: "Group name is required" } });
      return;
    }
    const isAdmin = form.groupType === "ADMIN";
    // Required items (must be Added via the buttons — a mere dropdown selection doesn't count).
    if (isAdmin && facilityAttachments.length === 0) {
      this.notify("Please add a facility to proceed.", "danger");
      return;
    }
    if (!isAdmin && facilityAttachments.length === 0 && childGroupIds.length === 0) {
      this.notify("Please add a facility or group to proceed.", "danger");
      return;
    }
    this.setState({ saving: true });
    try {
      const body = {
        groupName: form.groupName.trim(),
        groupType: form.groupType,
        facilities: facilityAttachments,
        childGroupIds: childGroupIds,
      };
      const r = await fetch(apiPath.groups, {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(body),
      });
      if (r.ok) {
        this.notify("Group created", "success");
        this.setState({ modalStatus: false, saving: false });
        this.fetchGroups();
        this.fetchFacilities();
      } else {
        this.notify(await this.extractError(r, "Could not create group"), "danger");
        this.setState({ saving: false });
      }
    } catch (e) {
      this.notify("Server error", "danger");
      this.setState({ saving: false });
    }
  };

  render() {
    const { groups, facilities, search, form, formErrors, facilityAttachments, childGroupIds } = this.state;
    const q = (search || "").toLowerCase();
    const filtered = q ? groups.filter((g) => (g.groupName || "").toLowerCase().includes(q)) : groups;

    return (
      <GridContainer>
        <ConfirmModal
          title="Delete Group"
          message={
            this.state.deleteTarget
              ? `Delete group "${this.state.deleteTarget.groupName}"? This removes its hierarchy links, facility attachments and manager assignments.`
              : ""
          }
          confirmModalStatus={this.state.confirmDeleteStatus}
          confirmModalClose={this.confirmDeleteClose}
          submit={this.doDeleteGroup}
          loading={this.state.deleting}
        />

        <Card className={"advert-cover"}>
          <GridItem xs={12} sm={12} md={12}>
            <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
              <h4>Region Groups</h4>
            </CardHeader>
          </GridItem>

          <div className={"custom-container"}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <div className="search-btn-cover" style={{ float: "none" }}>
                <input
                  type="text"
                  placeholder="Search groups by name."
                  className="search-btn"
                  onChange={(e) => this.setState({ search: e.target.value })}
                />
                <i className="material-icons">search</i>
              </div>
              <div className="button-cover" style={{ float: "none" }}>
                <button className="pointer btn1" onClick={this.modalOpen}>Add New Group</button>
              </div>
            </div>

            <div className="tableResponsive" style={{ clear: "both" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell>Manager</TableCell>
                    <TableCell>Group Type</TableCell>
                    <TableCell>Facilities</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length ? (
                    filtered.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell><strong>{g.groupName}</strong></TableCell>
                        <TableCell>
                          {g.managerNames && g.managerNames.length ? g.managerNames.join(", ") : "—"}
                        </TableCell>
                        <TableCell>{levelLabel(g.groupType)}</TableCell>
                        <TableCell>{g.facilityCount != null ? g.facilityCount : 0}</TableCell>
                        <TableCell align="center">
                          <div style={{ display: "flex", justifyContent: "center" }}>
                            <SwitchToggle id={g.id} status={g.active !== false} clickHandler={() => this.toggleStatus(g)} />
                          </div>
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {/* View action hidden for now
                          <span className="edit-action" title="View group" onClick={() => this.openView(g)} style={{ marginRight: 12 }}>
                            <Icon className="fa fa-eye" aria-hidden="true" />
                          </span>
                          */}
                          <span className="edit-action" title="Edit group" onClick={() => this.openEdit(g)} style={{ marginRight: 12 }}>
                            <Icon className="fa fa-pencil" aria-hidden="true" />
                          </span>
                          <span className="edit-action" title="Delete group" onClick={() => this.deleteGroup(g)}>
                            <Icon className="fa fa-trash-o" aria-hidden="true" />
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan="6"><div className="not-found-text">No groups found.</div></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>

        {/* Create Group dialog */}
        <Dialog
          open={this.state.modalStatus}
          maxWidth={"sm"}
          fullWidth
          onClose={this.modalClose}
          TransitionComponent={Transition}
          className="add-modal"
        >
          <DialogTitle className="add-modal-title">
            Create Group
            <IconButton className="closeButton" aria-label="close" onClick={this.modalClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" mb={2} mt={1}>
              <Box width={1 / 2} pr={1}>
                <TextField fullWidth name="groupName" value={form.groupName} onChange={this.handleField} label="Group Name *" />
                <FormErrors show={true} formErrors={formErrors} fieldName="groupName" />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField fullWidth select name="groupType" value={form.groupType} onChange={this.handleField} label="Group Level *">
                  {LEVELS.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                </TextField>
              </Box>
            </Box>

            <div style={{ fontWeight: 700, fontSize: "0.8rem", margin: "8px 0 4px", textTransform: "uppercase", color: "#6b6969" }}>
              Add groups or facilities under this group
            </div>

            {/* Attached facilities */}
            {facilityAttachments.map((a, idx) => (
              <div key={`f-${idx}`} className="dlg-list-row">
                <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                <span className="dlg-list-text">{this.facilityName(a.facilityId)}</span>
                <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.removeFacility(idx)} />
              </div>
            ))}
            {childGroupIds.map((id) => (
              <div key={`g-${id}`} className="dlg-list-row">
                <AccountTreeOutlinedIcon className="dlg-list-icon" />
                <span className="dlg-list-text">{this.groupNameById(id)}</span>
                <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.removeChild(id)} />
              </div>
            ))}

            {/* Add facility row */}
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1} pr={1}>
                <SearchableSelect
                  label="Facility"
                  placeholder="-- select facility --"
                  value={this.state.pickFacilityId}
                  onChange={(v) => this.setState({ pickFacilityId: v })}
                  options={facilities
                    .filter((f) => !facilityAttachments.some((a) => String(a.facilityId) === String(f.facilityId)))
                    .map((f) => ({ value: f.facilityId, label: f.groupName ? `${f.facilityName} — in ${f.groupName}` : f.facilityName }))}
                />
              </Box>
              <Button className="dlg-add-btn" onClick={this.addFacility}>Add facility</Button>
            </Box>

            {/* Add existing sub-group row — hidden for Admin groups (lowest level, no children) */}
            {form.groupType !== "ADMIN" && (
              <Box display="flex" alignItems="center" mt={1}>
                <Box flex={1} pr={1}>
                  <SearchableSelect
                    label="Existing group"
                    placeholder="-- select group --"
                    value={this.state.pickChildId}
                    onChange={(v) => this.setState({ pickChildId: v })}
                    options={this.eligibleChildGroups(form.groupType, childGroupIds, null)
                      .map((g) => ({ value: g.id, label: `${g.groupName} (${levelLabel(g.groupType)})` }))}
                  />
                </Box>
                <Button className="dlg-add-btn" onClick={this.addChild}>Add existing group</Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn1" onClick={this.createGroup} disabled={this.state.saving}>
              {this.state.saving && <CircularProgress size={24} className="buttonProgress" />}
              Create Group
            </Button>
            <Button onClick={this.modalClose} className="btn2" color="primary">Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* View group */}
        <Dialog open={this.state.viewOpen} maxWidth={"sm"} fullWidth onClose={this.closeView} TransitionComponent={Transition} className="add-modal">
          <DialogTitle className="add-modal-title">
            Group Details
            <IconButton className="closeButton" aria-label="close" onClick={this.closeView}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            {this.state.viewLoading || !this.state.viewDetail ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}><CircularProgress size={26} /></div>
            ) : (
              (() => {
                const d = this.state.viewDetail;
                const g = d.group || {};
                return (
                  <div style={{ fontSize: "0.88rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                      <strong>{g.groupName}</strong>
                      <span>{g.active === false ? "Inactive" : "Active"}</span>
                    </div>
                    <div style={{ color: "#6b6969", padding: "2px 0" }}>Level: {levelLabel(g.groupType)}</div>
                    <div style={{ color: "#6b6969", padding: "2px 0" }}>Parent: {g.parentGroupId ? this.groupNameById(g.parentGroupId) : "— (top level)"}</div>
                    <div style={{ color: "#6b6969", padding: "2px 0" }}>Facilities in subtree: {g.facilityCount != null ? g.facilityCount : 0}</div>

                    <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "12px 0 4px" }}>Child groups</div>
                    {d.children && d.children.length ? d.children.map((c) => (
                      <div key={c.id} className="dlg-list-row">
                        <AccountTreeOutlinedIcon className="dlg-list-icon" />
                        <span className="dlg-list-text">{c.groupName} <em style={{ color: "#6b6969" }}>({levelLabel(c.groupType)})</em></span>
                      </div>
                    )) : <div style={{ color: "#9a9a9a" }}>None</div>}

                    <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "12px 0 4px" }}>Facilities</div>
                    {d.facilities && d.facilities.length ? d.facilities.map((f) => (
                      <div key={f.id} className="dlg-list-row">
                        <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                        <span className="dlg-list-text">{f.facilityName || `#${f.facilityId}`}</span>
                      </div>
                    )) : <div style={{ color: "#9a9a9a" }}>None</div>}

                    <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "12px 0 4px" }}>Managers</div>
                    {d.managers && d.managers.length ? d.managers.map((m) => (
                      <div key={m.userId} style={{ padding: "2px 0" }}>👤 {((m.firstName || "") + " " + (m.lastName || "")).trim() || m.email} <em style={{ color: "#6b6969" }}>({m.status})</em></div>
                    )) : <div style={{ color: "#9a9a9a" }}>None</div>}
                  </div>
                );
              })()
            )}
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button onClick={this.closeView} className="btn2" color="primary">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit group */}
        <Dialog open={this.state.editOpen} maxWidth={"sm"} fullWidth onClose={this.closeEdit} TransitionComponent={Transition} className="add-modal">
          <DialogTitle className="add-modal-title">
            Edit Group
            <IconButton className="closeButton" aria-label="close" onClick={this.closeEdit}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" mb={2} mt={1} alignItems="center">
              <Box flex={1} pr={1}>
                <TextField fullWidth value={this.state.editName} onChange={(e) => this.setState({ editName: e.target.value })} label="Group Name *" />
              </Box>
              <Box pl={1} style={{ minWidth: 130 }}>
                <TextField fullWidth disabled label="Group Level" value={this.state.editGroup ? levelLabel(this.state.editGroup.groupType) : ""} />
              </Box>
            </Box>
            <Box display="flex" alignItems="center" mb={2}>
              <span style={{ marginRight: 10, color: "#6b6969" }}>Status:</span>
              {this.state.editGroup && (
                <SwitchToggle id={`edit-${this.state.editGroup.id}`} status={this.state.editActive} clickHandler={this.toggleEditActive} />
              )}
            </Box>

            {/* Child groups — hidden for Admin groups (lowest level, no children) */}
            {this.state.editGroup && this.state.editGroup.groupType !== "ADMIN" && (
              <>
                <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "8px 0 4px" }}>Child groups</div>
                {this.state.editDetail && this.state.editDetail.children && this.state.editDetail.children.map((c) => (
                  <div key={c.id} className="dlg-list-row">
                    <AccountTreeOutlinedIcon className="dlg-list-icon" />
                    <span className="dlg-list-text">{c.groupName} <em style={{ color: "#6b6969" }}>({levelLabel(c.groupType)})</em></span>
                    <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.editRemoveChild(c.id)} />
                  </div>
                ))}
                <Box display="flex" alignItems="center" mt={1}>
                  <Box flex={1} pr={1}>
                    <SearchableSelect
                      label="Existing group"
                      placeholder="-- select group --"
                      value={this.state.ePickChildId}
                      onChange={(v) => this.setState({ ePickChildId: v })}
                      options={(this.state.editGroup ? this.eligibleChildGroups(
                        this.state.editGroup.groupType,
                        ((this.state.editDetail && this.state.editDetail.children) || []).map((c) => c.id),
                        this.state.editGroup.id
                      ) : []).map((x) => ({ value: x.id, label: `${x.groupName} (${levelLabel(x.groupType)})` }))}
                    />
                  </Box>
                  <Button className="dlg-add-btn" onClick={this.editAddChild}>Add group</Button>
                </Box>
              </>
            )}

            {/* Facilities */}
            <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "12px 0 4px" }}>Facilities</div>
            {this.state.editDetail && this.state.editDetail.facilities && this.state.editDetail.facilities.map((f) => (
              <div key={f.id} className="dlg-list-row">
                <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                <span className="dlg-list-text">{f.facilityName || `#${f.facilityId}`}</span>
                <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.editRemoveFacility(f.id)} />
              </div>
            ))}
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1} pr={1}>
                <SearchableSelect
                  label="Facility"
                  placeholder="-- select facility --"
                  value={this.state.ePickFacilityId}
                  onChange={(v) => this.setState({ ePickFacilityId: v })}
                  options={facilities
                    .filter((f) => !((this.state.editDetail && this.state.editDetail.facilities) || []).some((af) => String(af.facilityId) === String(f.facilityId)))
                    .map((f) => ({ value: f.facilityId, label: f.groupName ? `${f.facilityName} — in ${f.groupName}` : f.facilityName }))}
                />
              </Box>
              <Button className="dlg-add-btn" onClick={this.editAddFacility}>Add facility</Button>
            </Box>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn1" onClick={this.saveEdit} disabled={this.state.editSaving}>
              {this.state.editSaving && <CircularProgress size={20} className="buttonProgress" />}
              Update
            </Button>
            <Button onClick={this.closeEdit} className="btn2" color="primary">Cancel</Button>
          </DialogActions>
        </Dialog>
      </GridContainer>
    );
  }
}
